import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { IError } from 'src/app/models/error.model';
import { IUser } from 'src/app/models/user.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { AuthService } from 'src/app/services/auth.service';
import { GlobalConfigService } from 'src/app/services/global-config.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { HyperDxService } from 'src/app/services/hyperdx.service';
import { IntercomService } from 'src/app/services/intercom.service';
import { ContextService } from 'src/app/services/context.service';

@Component({
  selector: 'app-temporary-password-modal',
  templateUrl: './temporary-password-modal.component.html',
  styleUrls: ['./temporary-password-modal.component.sass']
})
export class TemporaryPasswordModalComponent implements OnInit {
  @Input() email: string = '';
  @Input() temporaryPassword: string = '';
  @Input() isOpen: boolean = false;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSuccess = new EventEmitter<IUser>();

  loading = false;
  showNewPassword = false;
  showConfirmPassword = false;

  // Formulário de nova senha
  passwordForm: FormGroup = new FormGroup({
    newPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
    confirmPassword: new FormControl('', [Validators.required])
  });

  // Validações da senha
  passwordValidations = {
    hasLowercase: false,
    hasUppercase: false,
    hasMinLength: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService,
    private authService: AuthService,
    private router: Router,
    private hyperDxService: HyperDxService,
    private intercomService: IntercomService,
    private contextService: ContextService,
    private store: Store<{ warningPopup: IError }>
  ) {}

  ngOnInit(): void {
    // Escuta mudanças na senha para validar em tempo real
    this.passwordForm.get('newPassword')?.valueChanges.subscribe((value: string) => {
      this.validatePassword(value);
    });

    // Bloqueia a tecla Esc para não fechar o modal
    document.addEventListener('keydown', this.handleEscapeKey.bind(this));
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
  }

  private handleEscapeKey(event: KeyboardEvent): void {
    if (this.isOpen && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  validatePassword(password: string): void {
    this.passwordValidations = {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasMinLength: password.length >= 8,
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~';]/.test(password)
    };
  }

  isPasswordValid(): boolean {
    return Object.values(this.passwordValidations).every(v => v === true);
  }

  passwordsMatch(): boolean {
    const newPassword = this.passwordForm.get('newPassword')?.value;
    const confirmPassword = this.passwordForm.get('confirmPassword')?.value;
    return newPassword && confirmPassword && newPassword === confirmPassword;
  }

  canSubmit(): boolean {
    return this.isPasswordValid() && this.passwordsMatch() && !this.loading;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onBackdropClick(event: MouseEvent): void {
    // Não faz nada - modal não pode ser fechado clicando no backdrop
    event.stopPropagation();
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit()) return;

    this.loading = true;
    const newPassword = this.passwordForm.get('newPassword')?.value;

    const url = `${this.globalConfig.getApiUrl()}/users/auth/reset-temporary-password`;
    const payload = {
      email: this.email,
      temporary_password: this.temporaryPassword,
      new_password: newPassword
    };
console.log(payload)
    this.http.post<any>(url, payload).subscribe({
        
      next: (response) => {
        console.log('Reset password response:', response);
        this.loading = false;
        
        if (!response) {
          this.store.dispatch(setWarningText('Erro: resposta vazia do servidor.', 500));
          return;
        }

        // O response pode vir direto ou dentro de um wrapper
        const user: IUser = response;

        // Salva os tokens e dados do usuário
        this.saveUserData(user);

        // Emite evento de sucesso
        this.onSuccess.emit(user);

        // Fecha o modal
        this.isOpen = false;
        this.onClose.emit();

        // Navega para a dashboard
          this.router.navigate(['main', 'v2']);

        this.store.dispatch(setWarningText('Senha atualizada com sucesso!', 200));
      },
      error: (err) => {
        console.log(err)
        this.loading = false;
        // Captura a mensagem de erro do backend
        const errorMessage = err?.error?.message || err?.error || err?.message || 'Erro ao redefinir senha. Tente novamente.';
        this.store.dispatch(setWarningText(errorMessage, err?.status || 500));
      }
    });
  }

  private saveUserData(user: IUser): void {
    this.authService.clear();

    // O backend retorna access_token ou jwt_token
    const token = user.access_token || user.jwt_token || '';
    this.authService.setToken(token);
    this.authService.setRefreshToken(user.refresh_token || '');
    this.authService.setUsername(user.name || '');
    this.authService.setUserEmail(user.email || '');
    this.authService.setCompanyName(user.company?.corporate_name || '');
    this.authService.setPermission(user.permission || '');
    this.authService.setAnamneseAuthType(user.company?.contracted_anamnese_type || 'NOT_CONTRACTED');
    this.authService.setSendDocsType(user.company?.sign_delivery_type || 'DELIVERY_METHOD_SMS');
    this.authService.setUserId(user.id || '');
    this.authService.setModules(user.modules || []);
    this.authService.setUserImage(user.company?.logo || '');

    // Armazenar dados da clínica se disponíveis
    if (user.company?.cnpj) {
      this.authService.setClinicCnpj(user.company.cnpj);
    }
    if (user.company?.id) {
      this.authService.setClinicId(user.company.id);
    }
    // Armazena o JWT do Intercom gerado pelo backend (Identity Verification)
    this.authService.setIntercomJwt(user.intercom_jwt || '');
    this.authService.setPatientModelPermission(user.company?.is_model_patient_active || false);
    this.authService.setTreatmentPlanPermission(user.company?.has_treatment_plans || false);
    this.authService.setCalculatorPermission(user.company?.has_calculator || false);

    // Popula o ContextService com a clínica do login para o switcher mostrar imediatamente
    if (user.company?.cnpj && user.permission !== 'ROOT') {
      this.contextService.setActiveClinic({
        id: user.company.id || '',
        cnpj: user.company.cnpj,
        name: user.company.corporate_name || '',
        modules: (user.modules || []).map((m: any) => ({ id: m.id || '', description: m.description || '' })),
        hasTreatmentPlans: user.company?.has_treatment_plans || false,
        hasCalculator: user.company?.has_calculator || false,
        contractedAnamneseType: user.company?.contracted_anamnese_type || 'NOT_CONTRACTED',
        signDeliveryType: user.company?.sign_delivery_type || 'DELIVERY_METHOD_SMS',
        isModelPatientActive: user.company?.is_model_patient_active || false,
        logo: user.company?.logo || ''
      });
    }

    // Configura a identificação do usuário no HyperDX
    this.hyperDxService.setUserIdentification();

    // Inicializa o Intercom com os dados do usuário
    this.intercomService.boot();

    // Registra o evento de login após troca de senha temporária
    this.hyperDxService.logAction('user-login-temporary-password-reset', {
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      permission: user.permission,
      companyName: user.company?.corporate_name
    });
  }
}
